import * as React from 'react';
import { upperFirstLetter } from '../../helpers/string';
import { __ } from '../../helpers/TranslationService';
import { IFormattedCabin, IFormattedCabins, ISoldOutBooking } from '../../interfaces';
import { IRouterReducer } from '../../reducers/router';
import { CabinDetailsOverlay } from '../CabinDetailsOverlay/CabinDetailsOverlay';
import { CardGradient } from '../CardGradient/CardGradient';
import { Size } from '../Icons/Size';
import { Modal } from '../Modal';
import { Cabin } from './Cabin';
import { priceAvailability } from '../../helpers/priceHelpers';
import { PerPersonPriceCabin } from '../PerPersonPriceCabin';
import * as vars from '../../consts/variables';

let styles: Record<string, React.CSSProperties> = {
    price: {
        display: 'flex',
        fontSize: 14,
        margin: '10px 5px 0px',
        alignItems: 'center',
    },
    wrapperStyle: {
        width: '65%',
        marginLeft: 'auto',
        color: vars.softBlack,
    },
};

export interface ICabinSectionProps {
    cabins: IFormattedCabins;
    sailNid: number;
    router: IRouterReducer;
    isPriceShown?: boolean;
    learnMoreMode?: boolean;
    isCabinSelectShown?: boolean;
    discountThreshold?: number;
    isDesktop?: boolean;
    cheapestCabinId?: number;
    onCabinSelection?: (event: any) => void;
    selectedCabinId?: number;
    departure?: number;
    soldOutBooking?: ISoldOutBooking;
    includesFlight?: boolean;
}

export const CabinsSection = (props: ICabinSectionProps) => {
    const [modalVisible, setModalVisible] = React.useState(false);
    const [selectedCabin, setSelectedCabin] = React.useState<IFormattedCabin | undefined>();

    if (!props.cabins) {
        return null;
    }

    const cabinTypes = Object.keys(props.cabins).filter(
        (key) => props.cabins[key].cabins?.length > 0,
    );

    return (
        <>
            <div className="cabinSectionContainer">
                {modalVisible && selectedCabin && (
                    <Modal isOpen={modalVisible} onClose={() => setModalVisible(false)}>
                        <CabinDetailsOverlay
                            cabins={selectedCabin.cabins}
                            cabin={selectedCabin.cabins[0]}
                            urlBase={`${props.router.url}/${props.sailNid}`}
                            isPriceShown={props.isPriceShown}
                            isCabinSelectShown={props.isCabinSelectShown}
                        />
                    </Modal>
                )}
                {cabinTypes.map((key, index) => {
                    const cabin: IFormattedCabin = props.cabins[key];

                    const isSelectedCabin = Number(props.selectedCabinId) === cabin.cabinNid;

                    const cabinOnClickHandler = (cabinId) => {
                        if (props.onCabinSelection) {
                            props.onCabinSelection(cabinId);
                        } else {
                            setSelectedCabin(cabin);
                            setModalVisible(true);
                        }
                    };

                    if (props.isDesktop) {
                        const firstCabin = cabin.cabins?.[0]?.cabinType;
                        if (!firstCabin) return null;

                        const cabinKind = `${upperFirstLetter(key)}${
                            key !== 'suite' ? ' Cabin' : ''
                        }`;
                        const title = __(cabinKind, 'dreamlines');

                        const subtitle = (
                            <CabinCardSubtitle
                                max={firstCabin.maxPassengers}
                                size={firstCabin.size}
                            />
                        );

                        return (
                            <React.Fragment key={key}>
                                <CardGradient
                                    image={cabin.image.original}
                                    title={title}
                                    subTitle={subtitle}
                                    text={firstCabin.amenities.join(', ')}
                                    btnText={__('Cabin details', 'dreamlines')}
                                    onClick={() => {
                                        setSelectedCabin(cabin);
                                        setModalVisible(true);
                                    }}
                                />
                            </React.Fragment>
                        );
                    }

                    return (
                        <Cabin
                            key={key}
                            kind={key}
                            price={cabin.perPersonPrice}
                            discount={cabin.discountPercentage}
                            currency={cabin.currency}
                            cabins={cabin.cabins}
                            minPriceCabinNid={cabin.cabinNid}
                            url={props.router.url}
                            sailNid={props.sailNid}
                            isLastItem={index === cabinTypes.length - 1}
                            image={cabin.image}
                            isPriceShown={props.isPriceShown}
                            learnMoreMode={props.learnMoreMode}
                            isCabinSelectShown={props.isCabinSelectShown}
                            cheapestCabinId={props.cheapestCabinId}
                            onCabinSelection={cabinOnClickHandler}
                            isSelectedCabin={isSelectedCabin}
                        />
                    );
                })}
            </div>
            <div>
                {cabinTypes.map((key, index) => {
                    const cabin: IFormattedCabin = props.cabins[key];

                    const isSelectedCabin = Number(props.selectedCabinId) === cabin.cabinNid;

                    const availablePrice = priceAvailability(
                        props.departure,
                        props.soldOutBooking,
                        cabin.perPersonPrice,
                        cabin.currency,
                    );

                    if (isSelectedCabin) {
                        return (
                            <div style={styles.price} key={key}>
                                <span>{__('price', 'dreamlines')}:</span>
                                <PerPersonPriceCabin
                                    price={cabin.perPersonPrice}
                                    priceString={availablePrice.string}
                                    discount={cabin.discountPercentage}
                                    discountThreshold={props.discountThreshold}
                                    isHeader={true}
                                    wrapperStyle={styles.wrapperStyle}
                                    includesFlight={props.includesFlight}
                                    onRequest={!availablePrice.valid}
                                />
                            </div>
                        );
                    }
                    return null;
                })}
            </div>
        </>
    );
};

const CabinCardSubtitle = (props: { max: number; size: string }) => {
    return (
        <div className="cardSubtitle">
            <span>
                <span className="icon-people" />
                {`${__('max.', 'dreamlines')} ${props.max}`}
            </span>
            <span>
                <Size height="20" width="20" style={{ fill: 'white', color: 'white' }} />
                {props.size}
            </span>
        </div>
    );
};
